import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LaborUnitProvider } from "@/lib/laborUnit";
import { Toaster } from "@/components/ui/sonner";
import { Sun } from "lucide-react";

import Login from "@/pages/Login";
import MagicVerify from "@/pages/MagicVerify";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import AppShell from "@/components/AppShell";
import { isPublicHost } from "@/lib/publicHost";
import { PublicUnavailable } from "@/pages/PublicProposal";
import Today from "@/pages/Today";
import Pipeline from "@/pages/Pipeline";
import Awaiting from "@/pages/Awaiting";
import Inbox from "@/pages/Inbox";

function InboxRedirect() {
  // merge stage 1: the old Email Inbox lives inside the one Email Agent page; old links keep their draft / sub-tab
  const loc = useLocation();
  const q = new URLSearchParams(loc.search);
  const sub = q.get("tab");
  q.delete("tab");
  q.set("tab", "inbox");
  if (sub) q.set("sub", sub);
  return <Navigate to={`/email-agent?${q.toString()}`} replace />;
}
import EmailAgent from "@/pages/EmailAgent";
import Clients from "@/pages/Clients";
import ClientPage from "@/pages/ClientPage";
import Estimates from "@/pages/Estimates";
import Awarded from "@/pages/Awarded";
import Roles from "@/pages/Roles";
import HitRate from "@/pages/HitRate";
import WinTrend from "@/pages/WinTrend";
import HowTo from "@/pages/HowTo";
import HeatMap from "@/pages/HeatMap";
import Reports from "@/pages/Reports";
import Benchmarks from "@/pages/Benchmarks";
import MyWork from "@/pages/MyWork";
import Activity from "@/pages/Activity";
import BidWorkspace from "@/pages/BidWorkspace";
import Libraries from "@/pages/Libraries";
import Analytics from "@/pages/Analytics";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";
import Import from "@/pages/Import";
import EstimateIO from "@/pages/EstimateIO";
import ProposalPrint from "@/pages/ProposalPrint";
import PublicProposal from "@/pages/PublicProposal";
import ClientPortal from "@/pages/ClientPortal";
import BOEPrint from "@/pages/BOEPrint";
import Branding from "@/pages/Branding";
import CompanySettings from "@/pages/CompanySettings";
import AdminStorage from "@/pages/AdminStorage";
import Approvals from "@/pages/Approvals";
import Messages from "@/pages/Messages";
import ClaudeConnect from "@/pages/ClaudeConnect";
import ClaudeDownload from "@/pages/ClaudeDownload";
import ClaudeUpload from "@/pages/ClaudeUpload";
import { canAccessPath, firstPathForRole } from "@/lib/departments";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Sun className="w-7 h-7 animate-spin text-[#003E87] dark:text-[#F4C400]" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loader />;
  if (!user) {
    const next = location.pathname.startsWith("/claude/") ? `?next=${encodeURIComponent(location.pathname + location.search)}` : "";
    return <Navigate to={`/login${next}`} replace />;
  }
  if (!canAccessPath(user.role, location.pathname)) {
    return <Navigate to={firstPathForRole(user.role)} replace />;
  }
  return <AppShell>{children}</AppShell>;
}

function ProtectedBare({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/magic" element={<MagicVerify />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/p/:token" element={<PublicProposal />} />
      <Route path="/c/:token" element={<ClientPortal />} />
      <Route path="/claude/connect" element={<ClaudeConnect />} />
      <Route path="/claude/download/:token" element={<Protected><ClaudeDownload /></Protected>} />
      <Route path="/claude/upload/:token" element={<Protected><ClaudeUpload /></Protected>} />
      <Route path="/today" element={<Protected><Today /></Protected>} />
      <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
      <Route path="/leads/:leadId" element={<Protected><Pipeline /></Protected>} />
      <Route path="/awaiting" element={<Protected><Awaiting /></Protected>} />
      <Route path="/inbox" element={<InboxRedirect />} />
      <Route path="/email-agent" element={<Protected><EmailAgent /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/clients/:name" element={<Protected><ClientPage /></Protected>} />
      <Route path="/estimates" element={<Protected><Estimates /></Protected>} />
      <Route path="/awarded" element={<Protected><Awarded /></Protected>} />
      <Route path="/hit-rate" element={<Protected><HitRate /></Protected>} />
      <Route path="/win-trend" element={<Protected><WinTrend /></Protected>} />
      <Route path="/heat-map" element={<Protected><HeatMap /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/benchmarks" element={<Protected><Benchmarks /></Protected>} />
      <Route path="/how-to" element={<Protected><HowTo /></Protected>} />
      <Route path="/how-to/:key" element={<Protected><HowTo /></Protected>} />
      <Route path="/my-work" element={<Protected><MyWork /></Protected>} />
      <Route path="/activity" element={<Protected><Activity /></Protected>} />
      <Route path="/roles" element={<Protected><Roles /></Protected>} />
      <Route path="/bids/:bidId" element={<Protected><BidWorkspace /></Protected>} />
      <Route path="/bids/:bidId/proposal/print" element={<ProtectedBare><ProposalPrint /></ProtectedBare>} />
      <Route path="/bids/:bidId/boe/print" element={<ProtectedBare><BOEPrint /></ProtectedBare>} />
      <Route path="/libraries" element={<Protected><Libraries /></Protected>} />
      <Route path="/import" element={<Protected><Import /></Protected>} />
      <Route path="/estimate-io" element={<Protected><EstimateIO /></Protected>} />
      <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/team" element={<Protected><Team /></Protected>} />
      <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/suppressions" element={<Navigate to="/email-agent?tab=screened" replace />} />
      <Route path="/branding" element={<Protected><Branding /></Protected>} />
      <Route path="/company" element={<Protected><CompanySettings /></Protected>} />
      <Route path="/admin/storage" element={<Protected><AdminStorage /></Protected>} />
      <Route path="*" element={<Protected><Today /></Protected>} />
    </Routes>
  );
}

export default function App() {
  // On the Power Ray address only the client-facing pages exist — no shell, no sign-in, nothing that says GridForge.
  // Any other path there goes to the app's own address (a trimmed client link never lands on a login page).
  function LeaveForApp() {
    React.useEffect(() => { window.location.replace(process.env.REACT_APP_BACKEND_URL); }, []);
    return null;
  }
  if (isPublicHost()) {
    return (
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/p/:token" element={<PublicProposal />} />
            <Route path="/c/:token" element={<ClientPortal />} />
            <Route path="*" element={<LeaveForApp />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </div>
    );
  }
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
        <BrandingProvider>
        <AuthProvider>
          <LaborUnitProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </LaborUnitProvider>
        </AuthProvider>
        </BrandingProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}
